import { Power } from 'lucide-react';
import { useCallback } from 'react';

import { useTranslation } from '@/app/providers';
import { Button } from '@/shared/components/button';
import { BRAND } from '@/shared/constants/brand';
// import { BranchIndicator } from '@/widgets/branch-indicator/branch-indicator';
import { LanguageSelector } from '@/widgets/language-selector/language-selector';
import { ThemeToggle } from '@/widgets/theme-toggle/theme-toggle';
import { Tooltip } from '@/shared/components/tooltip/tooltip';

interface BackofficeHeaderProps {
  showLogout?: boolean;
  isLoggingOut?: boolean;
  onLogout?: () => void;
}

export function BackofficeHeader({
  showLogout = false,
  isLoggingOut = false,
  onLogout,
}: BackofficeHeaderProps) {
  const { t } = useTranslation();

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  }, []);

  return (
    <header className="z-50 shrink-0 border-b bg-black shadow-nav">
      <div className="mx-auto flex h-16 items-center justify-between px-4 sm:px-8 md:px-12">
        <div className="flex min-w-0 items-center gap-6 lg:gap-10">
          <div className="flex shrink-0 items-center">
            <img
              src={BRAND.LOGO_SRC}
              alt={BRAND.LOGO_ALT}
              className="h-10 w-auto object-contain"
            />
          </div>
          {/* <BranchIndicator /> */}
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          <Tooltip content={t('header.language')} position="bottom">
            <LanguageSelector variant="dark" />
          </Tooltip>
          <Tooltip content={t('header.theme')} position="bottom">
            <ThemeToggle variant="dark" className="h-8 w-8" />
          </Tooltip>
          <Tooltip content={t('header.fullscreen')} position="bottom">
            <button
              type="button"
              onClick={toggleFullscreen}
              className="hidden h-8 w-8 items-center justify-center rounded-lg border border-surface-border text-surface-muted transition-colors hover:bg-surface-hover hover:text-white sm:flex"
              aria-label={t('header.fullscreen')}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            </button>
          </Tooltip>
          {showLogout && onLogout && (
            <Button
              variant="danger"
              leftIcon={<Power className="h-4 w-4" />}
              isLoading={isLoggingOut}
              disabled={isLoggingOut}
              onClick={onLogout}
            >
              {t('backoffice2020.logout')}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
